import { InfoOutlined, SettingOutlined } from "@ant-design/icons";
import { App, Button, Select } from "antd";
import {
    memo,
    ReactNode,
    useCallback,
    useEffect,
    useRef,
    useState,
} from "react";
import Webcam from "react-webcam";

import { useMultiTap, useStableRef } from "@/hooks";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import { openReplayService } from "@/Services/OpenReplay";
import { BlockLoader } from "@/shared/ui/BlockLoader";
import { PrimaryText } from "@/shared/ui/Typography";
import { setSelectedCameraId } from "@/store/slices/config";
import { copyObjectToClipboard } from "@/utils/dom/copyObjectToClipboard";

import { CameraMode, ViewfinderProps } from "../../Camera.types";
import { useTapToFocus } from "../../hooks/useTapToFocus";
import {
    StyledActionsContainer,
    StyledCameraButton,
    StyledLoadingContainer,
    StyledTextButton,
} from "../../Styled";
import { CameraConfigModal } from "./components/ConfigModal";
import { ExposureControl } from "./components/ExposureControl";
import { FlashControl } from "./components/Flash";
import { ZoomControls } from "./components/ZoomControls";
import {
    findCameraWithFlash,
    getVideoConstraints,
    prepareExposure,
    waitForFocus,
} from "./PreviewMode.utils";
import {
    StyledCapturingCorners,
    StyledCapturingCornersInner,
    StyledCapturingHint,
    StyledCapturingOverlay,
    StyledConfigContainer,
    StyledExposureOverlay,
    StyledFocusIndicator,
    StyledPreviewContainer,
    StyledScanLine,
    StyledTopActions,
    StyledZoomOverlay,
} from "./Styled";

const excludeLabelName = ["front", "передня", "передняя"];

interface PreviewModeProps {
    Viewfinder?: React.ComponentType<ViewfinderProps>;
    levelOk: boolean;
    oriEnabled: boolean;
    onCancel: () => void;
    onCapture: ({ blob, mode }: { blob: Blob; mode: CameraMode }) => void;
    onUserMediaError: () => void;
    previewOverlay?: ReactNode;
}

export const PreviewMode = memo(
    ({
        Viewfinder,
        levelOk,
        oriEnabled,
        onCancel,
        onCapture,
        onUserMediaError,
        previewOverlay,
    }: PreviewModeProps) => {
        const { notification } = App.useApp();

        const dispatch = useAppDispatch();
        const { selectedCameraId, cameraConfig } = useAppSelector(
            (s) => s.config,
        );

        const webcamRef = useRef<Webcam>(null);
        const streamRef = useRef<MediaStream | null>(null);
        const captureInProgressRef = useRef(false);
        const userExposureRef = useRef<number | null>(null);

        // After user tap, focus has already been initiated —
        // do not trigger single-shot again, but still wait for stabilization
        const userFocusedRef = useRef(false);

        const [isReady, setIsReady] = useState(false);
        const [isCapturing, setIsCapturing] = useState(false);
        const [stream, setStream] = useState<MediaStream | null>(null);
        const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
        const [camKey, setCamKey] = useState(0);
        const [lastFocusPoint, setLastFocusPoint] = useState<{
            x: number;
            y: number;
        } | null>(null);

        const [configModalOpen, setConfigModalOpen] = useState(false);

        const { indicator: focusIndicator, handleTap: handleFocusTap } =
            useTapToFocus(stream, isReady && !isCapturing);

        const cameraConfigRef = useStableRef(cameraConfig);
        const getCameraReplayPayload = useCallback(
            (err: unknown, extra?: Record<string, string>) => {
                const track = streamRef.current?.getVideoTracks?.()[0];

                return {
                    errorMessage:
                        err instanceof Error ? err.message : String(err),
                    errorName: err instanceof Error ? err.name : typeof err,
                    selectedCameraId: String(selectedCameraId ?? ""),
                    cameraMode: String(cameraConfigRef.current?.mode ?? ""),
                    quality: String(cameraConfigRef.current?.quality ?? ""),
                    trackReadyState: String(track?.readyState ?? ""),
                    ...extra,
                };
            },
            // eslint-disable-next-line react-hooks/exhaustive-deps
            [selectedCameraId],
        );

        const stopCamera = useCallback(() => {
            streamRef.current?.getTracks().forEach((t) => t.stop());
            streamRef.current = null;
            setStream(null);

            const video = webcamRef.current?.video as
                | HTMLVideoElement
                | undefined;
            if (video) {
                try {
                    video.srcObject = null;
                } catch (err) {
                    console.error(err);

                    openReplayService.startOnError(
                        "camera_error",
                        getCameraReplayPayload(err, {
                            source: "stopCamera",
                            operation: "srcObject=null",
                        }),
                    );
                }
            }
        }, [getCameraReplayPayload]);

        useEffect(() => {
            return () => {
                stopCamera();
            };
        }, [stopCamera]);

        const cameraOptions = devices.map((d, idx) => ({
            value: d.deviceId,
            label: `Камера ${idx + 1}`,
        }));

        const handleUserExposureChange = useCallback((value: number | null) => {
            userExposureRef.current = value;
        }, []);

        const handleUserMedia = useCallback(
            async (s: MediaStream) => {
                streamRef.current = s;

                try {
                    const track = s.getVideoTracks()[0];
                    await track?.applyConstraints({
                        advanced: [
                            {
                                focusMode: "continuous",
                                exposureMode: "continuous",
                            },
                        ],
                    });
                } catch (err) {
                    openReplayService.startOnError(
                        "camera_error",
                        getCameraReplayPayload(err, {
                            source: "handleUserMedia",
                            operation: "applyConstraints",
                        }),
                    );

                    console.error(err);
                }

                setStream(s);
                setIsReady(true);
                userFocusedRef.current = false;

                try {
                    const all = await navigator.mediaDevices.enumerateDevices();
                    const videoDevices = all.filter(
                        (d) =>
                            d.kind === "videoinput" &&
                            !excludeLabelName.some((v) =>
                                d.label.toLowerCase().includes(v),
                            ),
                    );
                    setDevices(videoDevices);

                    if (
                        selectedCameraId &&
                        videoDevices.some(
                            (d) => d.deviceId === selectedCameraId,
                        )
                    ) {
                        return;
                    }

                    const flashCameraId =
                        await findCameraWithFlash(videoDevices);

                    const newCameraId =
                        flashCameraId ?? videoDevices[0]?.deviceId;

                    if (!newCameraId) return;

                    dispatch(setSelectedCameraId(newCameraId));
                } catch (err) {
                    console.error("enumerateDevices error:", err);

                    openReplayService.startOnError(
                        "camera_error",
                        getCameraReplayPayload(err, {
                            source: "handleUserMedia",
                            operation: "enumerateDevices",
                        }),
                    );

                    notification.error({ title: String(err) });
                }
                userExposureRef.current = null;
            },
            [notification, getCameraReplayPayload, selectedCameraId, dispatch],
        );

        const handleUserMediaError = useCallback(
            (err: string | DOMException) => {
                console.error("Camera error:", err);

                openReplayService.startOnError(
                    "camera_error",
                    getCameraReplayPayload(err, {
                        source: "handleUserMediaError",
                        operation: "getUserMedia",
                    }),
                );

                notification.error({
                    title: "Не вдалося запустити камеру",
                    description: String(err),
                });
                onUserMediaError();
            },
            [notification, getCameraReplayPayload, onUserMediaError],
        );

        const handleCapture = useCallback(
            async (mode: CameraMode) => {
                if (captureInProgressRef.current) return;

                const startedAt = performance.now();

                const video = webcamRef.current?.video as
                    | HTMLVideoElement
                    | undefined;
                const s = streamRef.current;

                if (!video || !s) return;

                captureInProgressRef.current = true;
                setIsCapturing(true);

                try {
                    const track = s.getVideoTracks()[0];
                    if (!track) {
                        throw new Error("No video track");
                    }

                    if (cameraConfig.focus) {
                        await waitForFocus(track, userFocusedRef.current);
                        userFocusedRef.current = false;
                    }

                    if (
                        cameraConfig.exposure &&
                        userExposureRef.current === null
                    ) {
                        await prepareExposure(track);
                    }

                    if (
                        "ImageCapture" in window &&
                        track.readyState === "live" &&
                        cameraConfig.mode === "captureImage"
                    ) {
                        try {
                            const imageCapture = new ImageCapture(track);

                            const blob = await imageCapture.takePhoto();

                            onCapture({ blob, mode });
                            return;
                        } catch (err) {
                            console.error("ImageCapture failed:", err);

                            openReplayService.startOnError(
                                "camera_error",
                                getCameraReplayPayload(err, {
                                    source: "handleCapture",
                                    operation: "ImageCapture",
                                    hasImageCapture: String(
                                        "ImageCapture" in window,
                                    ),
                                }),
                            );
                        }
                    }

                    const videoWidth = video.videoWidth;
                    const videoHeight = video.videoHeight;

                    if (!videoWidth || !videoHeight) {
                        throw new Error(
                            `Video dimensions are invalid: ${videoWidth}x${videoHeight}`,
                        );
                    }

                    const canvas = document.createElement("canvas");
                    const ctx = canvas.getContext("2d");
                    if (!ctx) {
                        throw new Error("Canvas 2D context is not available");
                    }

                    canvas.width = videoWidth;
                    canvas.height = videoHeight;

                    ctx.imageSmoothingEnabled = true;
                    ctx.imageSmoothingQuality = "high";
                    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

                    const blob = await new Promise<Blob>((resolve, reject) => {
                        const cleanup = () => {
                            canvas.width = 0;
                            canvas.height = 0;
                        };

                        canvas.toBlob(
                            (blob) => {
                                if (!blob) {
                                    cleanup();
                                    reject(
                                        new Error(
                                            "canvas.toBlob returned null",
                                        ),
                                    );
                                    return;
                                }

                                cleanup();
                                resolve(blob);
                            },
                            "image/jpeg",
                            0.95,
                        );
                    });

                    onCapture({ blob, mode });
                } catch (err) {
                    openReplayService.startOnError(
                        "camera_error",
                        getCameraReplayPayload(err, {
                            source: "handleCapture",
                            operation: "canvas",
                        }),
                    );

                    notification.error({
                        title: "Не вдалося зробити знімок",
                        description: String(err),
                    });
                } finally {
                    const durationSec = (
                        (performance.now() - startedAt) /
                        1000
                    ).toFixed(2);
                    console.log(`[handleCapture] took ${durationSec}s`);

                    captureInProgressRef.current = false;
                    setIsCapturing(false);
                }
            },
            [
                notification,
                cameraConfig.focus,
                cameraConfig.exposure,
                cameraConfig.mode,
                onCapture,
                getCameraReplayPayload,
            ],
        );

        const handleChangeCamera = useCallback(
            (deviceId: string) => {
                stopCamera();
                setIsReady(false);
                dispatch(setSelectedCameraId(deviceId));
                setCamKey((k) => k + 1);
                userExposureRef.current = null;
            },
            [dispatch, stopCamera],
        );

        const mainBacklight = !Viewfinder && oriEnabled && isReady;

        const handleInfo = () => {
            copyObjectToClipboard(
                {
                    stateDevices: devices,
                    cameraOptions,
                    selectedCameraId,
                },
                notification,
            );
        };

        const handleForceCapture = useMultiTap(
            () => {
                handleCapture(CameraMode.forceCaptureConfirm);
            },
            { taps: 3, delay: 600 },
        );

        const handleCaptureClick = () => {
            if (!isReady || isCapturing || captureInProgressRef.current) return;

            if (!oriEnabled) {
                handleCapture(CameraMode.captured);
                return;
            }

            if (levelOk) {
                handleCapture(CameraMode.captured);
                return;
            }

            handleForceCapture();
        };

        // After tap — mark manual focus to avoid re-triggering autofocus
        const handlePreviewTap = useCallback(
            async (e: React.PointerEvent<HTMLElement>) => {
                await handleFocusTap(e);
                userFocusedRef.current = true;
                // зберігаємо точку тапу щоб показати слайдер
                setLastFocusPoint({ x: e.clientX, y: e.clientY });
            },
            [handleFocusTap],
        );

        const onQualityChange = useCallback(() => {
            stopCamera();
            setIsReady(false);
            setCamKey((k) => k + 1);
        }, [stopCamera]);

        return (
            <>
                <StyledPreviewContainer
                    $backlight={mainBacklight}
                    $levelOk={levelOk}
                    onPointerDown={
                        isReady && !isCapturing ? handlePreviewTap : undefined
                    }
                >
                    {isReady && (
                        <StyledTopActions>
                            <FlashControl stream={stream} isReady={isReady} />
                        </StyledTopActions>
                    )}

                    <Webcam
                        key={camKey}
                        ref={webcamRef}
                        audio={false}
                        muted
                        playsInline
                        videoConstraints={getVideoConstraints(
                            selectedCameraId,
                            cameraConfig.quality,
                        )}
                        onUserMedia={handleUserMedia}
                        onUserMediaError={handleUserMediaError}
                        style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "contain",
                        }}
                    />

                    {isReady && Viewfinder && (
                        <Viewfinder
                            $levelOk={levelOk}
                            $oriEnabled={oriEnabled}
                        />
                    )}

                    {!isReady && (
                        <StyledLoadingContainer>
                            <BlockLoader />
                        </StyledLoadingContainer>
                    )}

                    <StyledCapturingOverlay $visible={isCapturing}>
                        <StyledCapturingCorners />
                        <StyledCapturingCornersInner />
                        <StyledScanLine />
                        <StyledCapturingHint>
                            <span>Не рухайте телефон</span>
                        </StyledCapturingHint>
                    </StyledCapturingOverlay>

                    {previewOverlay}

                    {isReady && (
                        <StyledZoomOverlay>
                            <ZoomControls stream={stream} isReady={isReady} />
                        </StyledZoomOverlay>
                    )}

                    {isReady && (
                        <StyledExposureOverlay>
                            {" "}
                            {/* позиція: right: 16px, center vertical */}
                            <ExposureControl
                                stream={stream}
                                isReady={isReady}
                                focusPoint={lastFocusPoint}
                                onUserChange={handleUserExposureChange}
                            />
                        </StyledExposureOverlay>
                    )}

                    {focusIndicator && (
                        <StyledFocusIndicator
                            key={focusIndicator.id}
                            $x={focusIndicator.x}
                            $y={focusIndicator.y}
                        >
                            <span />
                        </StyledFocusIndicator>
                    )}
                </StyledPreviewContainer>

                <StyledConfigContainer>
                    <Button
                        icon={<InfoOutlined />}
                        type="default"
                        size="large"
                        onClick={handleInfo}
                    />

                    <Select
                        size="large"
                        value={selectedCameraId}
                        options={cameraOptions}
                        onChange={handleChangeCamera}
                        style={{ width: "100%" }}
                        placeholder="Оберіть камеру"
                        loading={!isReady}
                        disabled={!isReady || isCapturing}
                    />
                </StyledConfigContainer>

                <StyledActionsContainer
                    justify="center"
                    align="center"
                    gap={32}
                >
                    <StyledTextButton
                        size="large"
                        onClick={onCancel}
                        type="text"
                    >
                        Скасувати
                    </StyledTextButton>

                    <StyledCameraButton
                        onClick={handleCaptureClick}
                        disabled={isCapturing}
                        $disabled={
                            !isReady || isCapturing || (!levelOk && oriEnabled)
                        }
                        type="text"
                    />

                    <Button
                        size="large"
                        icon={
                            <PrimaryText fontSize={24} textColor="white">
                                <SettingOutlined />
                            </PrimaryText>
                        }
                        type="text"
                        onClick={() => setConfigModalOpen(true)}
                    />
                </StyledActionsContainer>

                <CameraConfigModal
                    open={configModalOpen}
                    onClose={() => setConfigModalOpen(false)}
                    onQualityChange={onQualityChange}
                />
            </>
        );
    },
);

PreviewMode.displayName = "PreviewMode";
